import React, { useState, useEffect } from 'react';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Row,
  Col,
} from 'reactstrap';
import { empresasService } from '../../services/empresasService';

function EmpresaForm({ empresa, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    razonSocial: '',
    nombreComercial: '',
    rfc: '',
    domicilioFiscal: '',
    codigoPostal: '',
    regimenFiscal: '',
    telefono: '',
    correo: '',
    zonaHoraria: 'America/Mexico_City',
    activo: true,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (empresa) {
      setFormData({
        razonSocial: empresa.RazonSocial || empresa.razonSocial || '',
        nombreComercial: empresa.NombreComercial || empresa.nombreComercial || '',
        rfc: empresa.RFC || empresa.rfc || '',
        domicilioFiscal: empresa.DomicilioFiscal || empresa.domicilioFiscal || '',
        codigoPostal: empresa.CodigoPostal || empresa.codigoPostal || '',
        regimenFiscal: empresa.RegimenFiscal || empresa.regimenFiscal || '',
        telefono: empresa.Telefono || empresa.telefono || '',
        correo: empresa.Correo || empresa.correo || '',
        zonaHoraria: empresa.ZonaHoraria || empresa.zonaHoraria || 'America/Mexico_City',
        activo: empresa.Activo !== undefined ? empresa.Activo : (empresa.activo !== undefined ? empresa.activo : true),
      });
    }
  }, [empresa]);

  const validate = () => {
    const newErrors = {};

    // RazonSocial es requerido
    if (!formData.razonSocial.trim()) {
      newErrors.razonSocial = 'La razón social es requerida';
    } else if (formData.razonSocial.length > 200) {
      newErrors.razonSocial = 'La razón social no puede exceder 200 caracteres';
    }

    // NombreComercial opcional pero con límite
    if (formData.nombreComercial && formData.nombreComercial.length > 200) {
      newErrors.nombreComercial = 'El nombre comercial no puede exceder 200 caracteres';
    }

    // RFC opcional pero con validación
    if (formData.rfc) {
      if (formData.rfc.length < 12 || formData.rfc.length > 13) {
        newErrors.rfc = 'El RFC debe tener entre 12 y 13 caracteres';
      }
    }

    // DomicilioFiscal opcional pero con límite
    if (formData.domicilioFiscal && formData.domicilioFiscal.length > 500) {
      newErrors.domicilioFiscal = 'El domicilio fiscal no puede exceder 500 caracteres';
    }

    // CodigoPostal opcional pero debe ser válido (5 dígitos)
    if (formData.codigoPostal) {
      if (!/^\d{5}$/.test(formData.codigoPostal.trim())) {
        newErrors.codigoPostal = 'El código postal debe tener 5 dígitos';
      } else if (formData.codigoPostal.length > 10) {
        newErrors.codigoPostal = 'El código postal no puede exceder 10 caracteres';
      }
    }

    // RegimenFiscal opcional pero con límite
    if (formData.regimenFiscal && formData.regimenFiscal.length > 10) {
      newErrors.regimenFiscal = 'El régimen fiscal no puede exceder 10 caracteres';
    }

    // Telefono opcional pero con límite
    if (formData.telefono && formData.telefono.length > 20) {
      newErrors.telefono = 'El teléfono no puede exceder 20 caracteres';
    }

    // Correo opcional pero debe ser válido
    if (formData.correo) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.correo)) {
        newErrors.correo = 'El correo electrónico no es válido';
      } else if (formData.correo.length > 100) {
        newErrors.correo = 'El correo no puede exceder 100 caracteres';
      }
    }

    // ZonaHoraria con límite
    if (formData.zonaHoraria && formData.zonaHoraria.length > 50) {
      newErrors.zonaHoraria = 'La zona horaria no puede exceder 50 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const empresaData = {
        RazonSocial: formData.razonSocial.trim(),
        NombreComercial: formData.nombreComercial?.trim() || null,
        RFC: formData.rfc?.trim() || null,
        DomicilioFiscal: formData.domicilioFiscal?.trim() || null,
        CodigoPostal: formData.codigoPostal?.trim() || null,
        RegimenFiscal: formData.regimenFiscal?.trim() || null,
        Telefono: formData.telefono?.trim() || null,
        Correo: formData.correo?.trim() || null,
        ZonaHoraria: formData.zonaHoraria || 'America/Mexico_City',
      };

      let response;
      if (empresa) {
        // Actualizar
        const empresaId = empresa.EmpresaID || empresa.empresaID;
        empresaData.EmpresaID = empresaId;
        empresaData.Activo = formData.activo;
        response = await empresasService.update(empresaId, empresaData);
      } else {
        // Crear
        response = await empresasService.create(empresaData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.Message || 'Error al guardar la empresa');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert color="danger">{error}</Alert>}

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>
              Razón Social <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="razonSocial"
              value={formData.razonSocial}
              onChange={handleChange}
              invalid={!!errors.razonSocial}
              maxLength={200}
              required
            />
            {errors.razonSocial && (
              <div className="text-danger small">{errors.razonSocial}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Nombre Comercial</Label>
            <Input
              type="text"
              name="nombreComercial"
              value={formData.nombreComercial}
              onChange={handleChange}
              invalid={!!errors.nombreComercial}
              maxLength={200}
            />
            {errors.nombreComercial && (
              <div className="text-danger small">{errors.nombreComercial}</div>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>RFC</Label>
            <Input
              type="text"
              name="rfc"
              value={formData.rfc}
              onChange={handleChange}
              invalid={!!errors.rfc}
              maxLength={13}
              placeholder="12-13 caracteres"
            />
            {errors.rfc && (
              <div className="text-danger small">{errors.rfc}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Teléfono</Label>
            <Input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              invalid={!!errors.telefono}
              maxLength={20}
            />
            {errors.telefono && (
              <div className="text-danger small">{errors.telefono}</div>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>Correo Electrónico</Label>
            <Input
              type="email"
              name="correo"
              value={formData.correo}
              onChange={handleChange}
              invalid={!!errors.correo}
              maxLength={100}
            />
            {errors.correo && (
              <div className="text-danger small">{errors.correo}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Zona Horaria</Label>
            <Input
              type="text"
              name="zonaHoraria"
              value={formData.zonaHoraria}
              onChange={handleChange}
              invalid={!!errors.zonaHoraria}
              maxLength={50}
            />
            {errors.zonaHoraria && (
              <div className="text-danger small">{errors.zonaHoraria}</div>
            )}
            <small className="form-text text-muted">
              Ejemplo: America/Mexico_City
            </small>
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label>Domicilio Fiscal</Label>
        <Input
          type="textarea"
          name="domicilioFiscal"
          value={formData.domicilioFiscal}
          onChange={handleChange}
          invalid={!!errors.domicilioFiscal}
          maxLength={500}
          rows={3}
        />
        {errors.domicilioFiscal && (
          <div className="text-danger small">{errors.domicilioFiscal}</div>
        )}
      </FormGroup>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>Código Postal</Label>
            <Input
              type="text"
              name="codigoPostal"
              value={formData.codigoPostal}
              onChange={handleChange}
              invalid={!!errors.codigoPostal}
              maxLength={10}
              placeholder="Ej: 01000"
              pattern="[0-9]{5}"
            />
            {errors.codigoPostal && (
              <div className="text-danger small">{errors.codigoPostal}</div>
            )}
            <small className="form-text text-muted">
              Código postal de 5 dígitos (requerido para facturación)
            </small>
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Régimen Fiscal</Label>
            <Input
              type="text"
              name="regimenFiscal"
              value={formData.regimenFiscal}
              onChange={handleChange}
              invalid={!!errors.regimenFiscal}
              maxLength={10}
              placeholder="Ej: 616"
            />
            {errors.regimenFiscal && (
              <div className="text-danger small">{errors.regimenFiscal}</div>
            )}
            <small className="form-text text-muted">
              Código del régimen fiscal según el SAT (ej: 616 = General de Ley Personas Morales)
            </small>
          </FormGroup>
        </Col>
      </Row>

      {empresa && (
        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              name="activo"
              checked={formData.activo}
              onChange={handleChange}
            />
            <span className="form-check-sign">
              <span className="check" />
            </span>
            Empresa Activa
          </Label>
        </FormGroup>
      )}

      <div className="text-right mt-4">
        <Button color="secondary" onClick={onCancel} className="mr-2">
          Cancelar
        </Button>
        <Button color="primary" type="submit" disabled={loading}>
          {loading ? 'Guardando...' : empresa ? 'Actualizar' : 'Crear'}
        </Button>
      </div>
    </Form>
  );
}

export default EmpresaForm;

